import { api } from '../../api.ts';
import { agentCounts, agentsApi, byUrgency } from '../../agents-api.ts';
import type { AgentInfo } from '../../agents-api.ts';
import type { TalkState } from '../../talk-audio.ts';
import { h } from '../../ui.ts';
import { lastActivity, statusBadge } from '../agents/parts.ts';
import { counts, icon, itemCard } from './kit.ts';
import type { TalkSession, WorkItem } from './kit.ts';

export const STATE: Record<TalkState, string> = {
  connecting: 'CONNECTING',
  listening: 'LISTENING · TALK ANY TIME',
  reconnecting: 'RECONNECTING',
  stopped: 'TALK ENDED',
};

export function orb(live: boolean): HTMLElement {
  const bars = [1, 2, 3, 4, 5, 6].map(() => h('i'));
  return h('div', { class: `p-orb ${live ? 'live' : ''}` }, h('div', {}, h('div', {}, ...bars)));
}

const n = (k: number, w: string) => `${k} ${w}${k === 1 ? '' : 's'}`;

/** An agent started from this talk or chat, as a card that opens it. */
export function agentCard(a: AgentInfo): HTMLElement {
  return h(
    'a',
    { class: `p-card ${a.status === 'asking' ? 'amber' : ''}`, href: `#/agent/${encodeURIComponent(a.id)}` },
    h(
      'span',
      { class: 'p-text' },
      h('span', { class: 'row' }, statusBadge(a.status), h('span', { class: 'truncate grow' }, a.title || a.task)),
      h('span', { class: 'p-muted truncate' }, a.question?.text ?? lastActivity(a)),
    ),
    icon.right(16),
  );
}

/** What a live talk shows, kept current from the daemon's events and a poll of the talk's work and agents. */
export class TalkScreen {
  readonly orb: HTMLElement;
  readonly state: HTMLElement;
  readonly chips = h('div', { class: 'p-chips' });
  readonly captions = h('section', { class: 'p-captions', 'aria-label': 'Captions', 'aria-live': 'polite' });
  readonly cards = h('section', { class: 'p-cards p-talk-cards', 'aria-label': 'What she started and filed' });
  readonly driveLine = h('div', { class: 'p-big' });
  private items: WorkItem[] = [];
  private agents: AgentInfo[] = [];
  private lines: Array<{ who: string; text: string }> = [];

  constructor(
    readonly s: TalkSession,
    initial: TalkState,
  ) {
    this.orb = orb(initial === 'listening');
    this.state = h('div', { class: 'p-state' }, STATE[initial === 'stopped' ? 'connecting' : initial]);
  }

  async refresh() {
    const [work, agents] = await Promise.all([
      api.get<{ items: WorkItem[] }>(`/sessions/${this.s.id}/work`).catch(() => null),
      agentsApi.list().catch(() => null),
    ]);
    this.items = work?.items ?? this.items;
    this.agents = agents ? agents.filter((a) => a.talkSessionId === this.s.id).sort(byUrgency) : this.agents;
    this.draw();
  }

  private draw() {
    const c = counts(this.items);
    const ac = agentCounts(this.agents);
    const working = c.working + ac.running;
    const needs = c.needs + ac.asking;
    const ready = c.ready + ac.previews;
    this.chips.replaceChildren(
      h('span', { class: 'badge accent' }, `${working} building`),
      needs ? h('span', { class: 'badge warning' }, `${needs} needs you`) : '',
      h('span', { class: 'badge outline' }, n(ready, 'preview')),
    );
    this.cards.replaceChildren(
      ...this.agents.slice(0, 3).map(agentCard),
      ...this.items.slice().reverse().slice(0, 3).map(itemCard),
    );
    const any = this.items.length + this.agents.length;
    this.driveLine.replaceChildren(
      h('span', {}, working ? `${working} building` : any ? 'Nothing building' : 'Listening'),
      needs ? h('span', { class: 'amber' }, `${needs} need${needs === 1 ? 's' : ''} you`) : '',
      ready ? h('span', {}, `${n(ready, 'preview')} ready`) : '',
    );
  }

  caption(who: string, text: string) {
    const last = this.lines.at(-1);
    if (last && last.who === who) last.text += ` ${text}`;
    else this.lines.push({ who, text });
    while (this.lines.length > 2) this.lines.shift();
    this.captions.replaceChildren(
      ...this.lines.map((l) =>
        h(
          'p',
          {},
          h('b', { class: l.who === 'assistant' ? 'her' : '' }, l.who === 'assistant' ? 'Assistant' : 'You'),
          l.text,
        ),
      ),
    );
  }

  onState(st: TalkState) {
    this.state.textContent = STATE[st];
    this.orb.classList.toggle('live', st === 'listening');
    this.orb.classList.toggle('off', st === 'reconnecting');
  }
}
